///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Owns automated camera motion: flying to saved views (TourPlayer), the
/// slow idle/manual orbit (OrbitBehavior), multi-stop story playback
/// (StoryPlayer, which sequences the same TourPlayer legs), and the
/// device-location follow / heading modes (FollowBehavior).
///
/// Created by EarthActivity and ticked from a frame task. Direct user
/// input always wins: any pointer/wheel/touch/key input cancels the
/// current flight or orbit instantly (no snap - automated motion and the
/// gesture handlers share the same live camera state).
///
/// Mode changes are mirrored to dialogSlice.cameraMode (ephemeral) so UI
/// like the orbit button can reflect the active state; per-frame progress
/// never touches redux (the orbit publishes its pose at a limited rate so
/// the compass tracks it; see OrbitBehavior).
///

import { CameraParams, type EarthCameraState } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import type { StoryPlayCommandData } from "@/redux/earthBrowserSlice"
import { FollowBehavior, type FollowMode } from "./FollowBehavior"
import { IdleMonitor } from "./IdleMonitor"
import { OrbitBehavior } from "./OrbitBehavior"
import { enableSceneLayers, focusSceneTarget } from "./sceneState"
import { StoryPlayer } from "./StoryPlayer"
import { TourPlayer } from "./TourPlayer"

// Orbit angular velocity (rad/s) used when no store provides tourSettings
// (embed builds); matches cDefaultTourSettings.orbitSpeed
const cDefaultOrbitSpeed = 0.02

export class CameraDirector {
	private context: EarthBrowserContext
	private cameraState: EarthCameraState
	private tourPlayer: TourPlayer
	private orbitBehavior: OrbitBehavior
	private followBehavior: FollowBehavior
	private storyPlayer: StoryPlayer
	private idleMonitor: IdleMonitor

	/** View waiting for a focus-release transition to finish before flying */
	private pendingViewId: string | null = null
	private lastTickTime?: number

	/**
	 * When automated motion (a flight) last ended. Auto-orbit waits for
	 * the idle delay after both user input AND automated motion, so the
	 * camera doesn't start drifting right after landing on a saved view.
	 */
	private lastMotionEndTime = 0

	constructor(context: EarthBrowserContext, cameraState: EarthCameraState) {
		this.context = context
		this.cameraState = cameraState
		this.tourPlayer = new TourPlayer(cameraState)
		this.orbitBehavior = new OrbitBehavior(cameraState)
		this.followBehavior = new FollowBehavior(context, cameraState, this.tourPlayer)
		this.storyPlayer = new StoryPlayer(context, this.tourPlayer, this.orbitBehavior)
		this.idleMonitor = new IdleMonitor(context.canvas)
		this.idleMonitor.onInput(() => this.handleUserInput())
		this.tourPlayer.onLegComplete = () => {
			this.lastMotionEndTime = performance.now()
		}
	}

	public destroy(): void {
		this.idleMonitor.destroy()
	}

	/**
	 * Fly to a saved view. Applies the view's layer dependencies first
	 * (enable-only). A view saved with a focused entity refocuses that
	 * entity - the focus transition frames it and focusSceneObject loads
	 * it on demand, so it exists even when its layer was just enabled.
	 * Otherwise, if another entity is focused, focus is returned to Earth
	 * first and the pose flight starts once that transition completes.
	 */
	public flyToView(viewId: string): void {
		const view = engineBridge.getState()?.savedViews.views[viewId]
		if (!view) return

		this.storyPlayer.stopPlayback()
		this.stopOrbit()
		this.followBehavior.stop()
		enableSceneLayers(view.layers)

		if (view.focusTargetId && focusSceneTarget(view.focusTargetId)) {
			this.pendingViewId = null
			this.tourPlayer.stop()
			return
		}

		const focusManager = this.context.focusManager
		const focused = focusManager.getFocusedNode()
		if (focused && focused.id !== "earth") {
			focusManager.releaseFocus({ preserveCurrentState: true })
		}
		this.pendingViewId = viewId
	}

	/**
	 * Place the camera at an exact pose immediately (launch ?camera=
	 * deep link: a shared URL should land there, not fly from the
	 * recipient's persisted position).
	 */
	public setPoseImmediate(params: CameraParams): void {
		this.stopAllMotion()
		this.cameraState.setParamsImmediate(params)
		this.cameraState.saveCameraState()
	}

	/** Route "story/*" camera commands to the story player */
	public handleStoryCommand(command: string, data?: unknown): void {
		switch (command) {
			case "story/play": {
				const payload = data as StoryPlayCommandData | undefined
				if (!payload?.storyId) return
				this.stopOrbit()
				this.followBehavior.stop()
				this.pendingViewId = null
				this.tourPlayer.stop()
				this.storyPlayer.play(payload.storyId, payload.stopIndex ?? 0, payload.guided ?? false)
				break
			}
			case "story/stop":
				this.storyPlayer.stopPlayback()
				break
			case "story/pause":
				this.storyPlayer.pause()
				break
			case "story/resume":
				this.storyPlayer.resume()
				break
			case "story/next":
				this.storyPlayer.next()
				break
			case "story/prev":
				this.storyPlayer.prev()
				break
		}
	}

	/** Manual orbit toggle (orbit button, command palette) */
	public toggleOrbit(): void {
		if (this.orbitBehavior.orbiting) {
			this.stopOrbit()
		} else {
			this.pendingViewId = null
			this.storyPlayer.stopPlayback()
			this.tourPlayer.stop()
			this.followBehavior.stop()
			this.startOrbit()
		}
	}

	/**
	 * "location/*" camera commands (my-location button). follow / heading
	 * enter the device-location modes (FollowBehavior flies to the fix once
	 * one exists); stop leaves them with the camera where it is
	 */
	public handleLocationCommand(command: string): void {
		switch (command) {
			case "location/follow":
			case "location/heading": {
				const mode: FollowMode = command === "location/heading" ? "heading" : "follow"
				if (this.followBehavior.currentMode === mode) return
				if (!this.followBehavior.active) {
					this.pendingViewId = null
					this.storyPlayer.stopPlayback()
					this.tourPlayer.stop()
					this.stopOrbit()
					// A focused entity owns the camera; hand it back to Earth
					// first (the behavior waits out the transition)
					const focusManager = this.context.focusManager
					const focused = focusManager.getFocusedNode()
					if (focused && focused.id !== "earth") {
						focusManager.releaseFocus({ preserveCurrentState: true })
					}
				}
				this.followBehavior.start(mode)
				break
			}
			case "location/stop":
				this.followBehavior.stop()
				break
		}
	}

	/** Whether the camera is in a device-location mode */
	public get following(): FollowMode | null {
		return this.followBehavior.currentMode
	}

	/**
	 * A camera command issued from the HUD (zoom, 2D/3D, compass, fly-to).
	 * Those don't touch the canvas, so the idle monitor never sees them;
	 * treat them exactly like a gesture: the orbit (which rewrites the pose
	 * every frame and would swallow the command) and any flight stop
	 * instantly, a playing story pauses, location following ends, and the
	 * idle clock resets so auto-orbit doesn't resume mid-animation.
	 */
	public interruptForUserInput(): void {
		this.followBehavior.stop()
		this.idleMonitor.recordInput()
	}

	/** Stop every kind of automated motion (palette "Stop Camera Motion") */
	public stopAllMotion(): void {
		this.pendingViewId = null
		this.storyPlayer.stopPlayback()
		this.tourPlayer.stop()
		this.stopOrbit()
		this.followBehavior.stop()
	}

	/** Per-frame tick, registered as a frame task by EarthActivity */
	public update(): void {
		const now = performance.now()
		const dt = this.lastTickTime ? Math.min(0.1, (now - this.lastTickTime) / 1000) : 0
		this.lastTickTime = now

		// An engaged story owns all automated motion (it sequences the
		// same TourPlayer/OrbitBehavior used below)
		if (this.storyPlayer.engaged) {
			this.storyPlayer.update(dt)
			return
		}

		// A pending flight starts once no focus transition is running (the
		// transition controls the camera until then, and Earth reacquires
		// its camera when the transition lands)
		if (this.pendingViewId) {
			if (this.context.focusManager.isTransitioning()) return

			const view = engineBridge.getState()?.savedViews.views[this.pendingViewId]
			this.pendingViewId = null
			if (view) {
				const c = view.camera
				this.tourPlayer.startLeg(new CameraParams(c.radius, c.theta, c.phi, c.tilt, c.bearing))
			}
			return
		}

		if (this.orbitBehavior.orbiting) {
			this.orbitBehavior.update(dt, engineBridge.getState()?.earthbrowser.tourSettings.orbitSpeed ?? cDefaultOrbitSpeed)
			return
		}

		// Location following: the behavior starts its entry flight on the
		// tour player and tracks the fix per frame once it lands. No
		// auto-orbit while it holds the camera
		if (this.followBehavior.active) {
			this.followBehavior.update(dt)
			this.tourPlayer.update(dt)
			return
		}

		this.tourPlayer.update(dt)

		if (!this.tourPlayer.playing) {
			this.maybeStartAutoOrbit(now)
		}
	}

	/** Start the idle orbit when enabled and everything has been quiet */
	private maybeStartAutoOrbit(now: number): void {
		const tourSettings = engineBridge.getState()?.earthbrowser.tourSettings
		if (!tourSettings) return
		const { autoOrbit, idleDelaySeconds } = tourSettings
		if (!autoOrbit) return
		// A paused story keeps its card up; don't drift away from it
		if (this.storyPlayer.hasStory) return
		if (this.idleMonitor.idleSeconds < idleDelaySeconds) return
		if ((now - this.lastMotionEndTime) / 1000 < idleDelaySeconds) return
		if (this.context.focusManager.isTransitioning()) return

		this.startOrbit()
	}

	private startOrbit(): void {
		this.orbitBehavior.start()
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: "orbit" })
	}

	private stopOrbit(): void {
		if (!this.orbitBehavior.orbiting) return
		this.orbitBehavior.stop()
		this.lastMotionEndTime = performance.now()
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: "free" })
	}

	/** Any user input cancels automated motion instantly */
	private handleUserInput(): void {
		this.pendingViewId = null
		// A playing story pauses (resumable from the card) instead of
		// ending: grabbing the globe mid-story is exploration, not exit
		if (this.storyPlayer.engaged) {
			this.storyPlayer.pause()
			return
		}
		// Location following survives a touch-down (a tap, or a finger
		// resting on the globe): it ends itself only once a gesture moves
		// the camera, so the view isn't lost to an accidental touch. A
		// touch during the entry flight cancels the flight and the mode
		if (this.followBehavior.active && this.tourPlayer.playing) {
			this.followBehavior.stop()
		}
		this.tourPlayer.stop()
		this.stopOrbit()
	}
}
